import React, { useState, useCallback, useEffect } from 'react';
import type { WorkflowConfig, WeeklySchedule, DaySchedule, TimeRange } from '../siteApi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Clock, Trash2, CheckCircle } from 'lucide-react';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TOTAL_BLOCKS = 96; // 24 hours * 4 (15-minute blocks)
const DISPLAY_ORDER = [0, 1, 2, 3, 4, 5, 6]; // Monday to Sunday display order

// Colors for different workflows
const WORKFLOW_COLORS = [
  'bg-green-500',
  'bg-blue-500',
  'bg-purple-500',
  'bg-yellow-500',
  'bg-red-500',
  'bg-indigo-500',
  'bg-pink-500',
  'bg-orange-500',
];

// Convert block index to time string (HH:MM:SS)
const blockToTimeString = (blockIdx: number): string => {
  const totalMinutes = blockIdx * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};

// Convert time string (HH:MM:SS) to block index
const timeStringToBlock = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return (hours * 60 + minutes) / 15;
};

interface WorkflowScheduleProps {
  workflows: WorkflowConfig[];
  selectedWorkflowIndex: number;
  onScheduleChange: (workflowIndex: number, schedule: WeeklySchedule) => void;
  onDefaultWorkflowChange?: (workflowIndex: number) => void;
}

interface SelectionPoint {
  day: number; // We keep this as number for internal selection tracking
  block: number;
}

export const WorkflowSchedule: React.FC<WorkflowScheduleProps> = ({
  workflows,
  selectedWorkflowIndex,
  onScheduleChange,
  onDefaultWorkflowChange,
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<SelectionPoint | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<SelectionPoint | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [viewWorkflowIndex, setViewWorkflowIndex] = useState(selectedWorkflowIndex);

  // Get the current workflow's schedule or create an empty one
  const getWorkflowSchedule = useCallback(
    (index: number): WeeklySchedule => {
      const workflow = workflows[index];
      if (!workflow) return { days: [] };

      return workflow.schedule || { days: [] };
    },
    [workflows]
  );

  // Get color for a workflow based on its index
  const getWorkflowColor = (index: number): string => {
    return WORKFLOW_COLORS[index % WORKFLOW_COLORS.length];
  };

  // Find the default workflow index
  const getDefaultWorkflowIndex = useCallback((): number => {
    const defaultIndex = workflows.findIndex((workflow) => workflow.isDefault);
    return defaultIndex >= 0 ? defaultIndex : -1;
  }, [workflows]);

  // Handle changing the default workflow
  const handleDefaultWorkflowChange = (index: number) => {
    if (onDefaultWorkflowChange) {
      onDefaultWorkflowChange(index);
    }
  };

  // Create a map of which blocks are scheduled for each workflow
  const createScheduleMap = () => {
    const scheduleMap: Record<number, Record<number, number[]>> = {};

    // Initialize empty map
    DAYS_OF_WEEK.forEach((_, dayIndex) => {
      scheduleMap[dayIndex] = {};
      for (let blockIdx = 0; blockIdx < TOTAL_BLOCKS; blockIdx++) {
        scheduleMap[dayIndex][blockIdx] = [];
      }
    });

    // Fill in the map with workflow indices
    workflows.forEach((workflow, workflowIndex) => {
      if (workflow.schedule) {
        workflow.schedule.days.forEach((daySchedule) => {
          // Convert day string to index
          const dayName = daySchedule.day;
          const dayIndex = DAYS_OF_WEEK.findIndex((day) => day === dayName);

          // Skip if day name is invalid
          if (dayIndex === -1) return;

          daySchedule.timeRanges.forEach((timeRange) => {
            const startBlock = timeStringToBlock(timeRange.start);
            const endBlock = timeStringToBlock(timeRange.end);

            for (let blockIdx = startBlock; blockIdx <= endBlock; blockIdx++) {
              if (scheduleMap[dayIndex] && scheduleMap[dayIndex][blockIdx]) {
                scheduleMap[dayIndex][blockIdx].push(workflowIndex);
              }
            }
          });
        });
      }
    });

    return scheduleMap;
  };

  const scheduleMap = createScheduleMap();

  // Check if a block is part of the current selection
  const isBlockSelected = useCallback(
    (dayIndex: number, blockIdx: number): boolean => {
      if (!selectionStart || !selectionEnd) return false;

      const startDay = Math.min(selectionStart.day, selectionEnd.day);
      const endDay = Math.max(selectionStart.day, selectionEnd.day);
      const startBlock = Math.min(selectionStart.block, selectionEnd.block);
      const endBlock = Math.max(selectionStart.block, selectionEnd.block);

      return (
        dayIndex >= startDay && dayIndex <= endDay && blockIdx >= startBlock && blockIdx <= endBlock
      );
    },
    [selectionStart, selectionEnd]
  );

  // Handle selection completion
  const handleSelection = useCallback(() => {
    if (!selectionStart || !selectionEnd || viewWorkflowIndex === undefined) return;

    const startDay = Math.min(selectionStart.day, selectionEnd.day);
    const endDay = Math.max(selectionStart.day, selectionEnd.day);
    const startBlock = Math.min(selectionStart.block, selectionEnd.block);
    const endBlock = Math.max(selectionStart.block, selectionEnd.block);

    // Get current schedule
    const currentSchedule = getWorkflowSchedule(viewWorkflowIndex);
    const newDays: DaySchedule[] = [...(currentSchedule.days || [])];

    // Process each day in the selection
    for (let dayIndex = startDay; dayIndex <= endDay; dayIndex++) {
      // Convert day index to day name
      const dayName = DAYS_OF_WEEK[dayIndex];

      // Find existing day schedule or create a new one
      let daySchedule = newDays.find((d) => d.day === dayName);
      if (!daySchedule) {
        daySchedule = { day: dayName, timeRanges: [] };
        newDays.push(daySchedule);
      }

      // Ensure daySchedule is defined before using it
      if (!daySchedule) continue;

      // Convert block selection to time range
      const newTimeRange: TimeRange = {
        start: blockToTimeString(startBlock),
        end: blockToTimeString(endBlock),
      };

      // Add the new time range
      daySchedule.timeRanges.push(newTimeRange);
    }

    // Update the schedule
    const newSchedule: WeeklySchedule = { days: newDays };
    onScheduleChange(viewWorkflowIndex, newSchedule);
  }, [selectionStart, selectionEnd, viewWorkflowIndex, onScheduleChange, getWorkflowSchedule]);

  // Mouse event handlers
  const handleMouseDown = (dayIndex: number, blockIndex: number) => {
    setIsSelecting(true);
    setIsDragging(false);
    setSelectionStart({ day: dayIndex, block: blockIndex });
    setSelectionEnd({ day: dayIndex, block: blockIndex });
  };

  const handleMouseMove = (dayIndex: number, blockIndex: number) => {
    if (isSelecting) {
      setIsDragging(true);
      setSelectionEnd({ day: dayIndex, block: blockIndex });
    }
  };

  const handleMouseEnter = (dayIndex: number, blockIndex: number) => {
    if (isSelecting) {
      setIsDragging(true);
      setSelectionEnd({ day: dayIndex, block: blockIndex });
    }
  };

  // Add global mouse up handler
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting && selectionStart && selectionEnd && isDragging) {
        handleSelection();
      }
      setIsSelecting(false);
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isSelecting, selectionStart, selectionEnd, isDragging, handleSelection]);

  // Update view workflow when selected workflow changes
  useEffect(() => {
    setViewWorkflowIndex(selectedWorkflowIndex);
  }, [selectedWorkflowIndex]);

  // Clear schedule for the current workflow
  const clearSchedule = () => {
    onScheduleChange(viewWorkflowIndex, { days: [] });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium uppercase text-gray-400">Workflow Schedule</h3>

        <div className="flex items-center gap-2">
          {onDefaultWorkflowChange && (
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-400">Default:</div>
              <Select
                value={getDefaultWorkflowIndex().toString()}
                onValueChange={(value) => handleDefaultWorkflowChange(parseInt(value))}
              >
                <SelectTrigger className="w-[150px] border-gray-700 bg-[#222] text-white">
                  <SelectValue placeholder="Select default" />
                </SelectTrigger>
                <SelectContent className="border-gray-700 bg-[#222] text-white">
                  {workflows.map((workflow, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {workflow.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-400">Selection:</div>
            <Select
              value={viewWorkflowIndex.toString()}
              onValueChange={(value) => setViewWorkflowIndex(parseInt(value))}
            >
              <SelectTrigger className="w-[180px] border-gray-700 bg-[#222] text-white">
                <SelectValue placeholder="Select workflow" />
              </SelectTrigger>
              <SelectContent className="border-gray-700 bg-[#222] text-white">
                {workflows.map((workflow, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {workflow.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" variant="outline" onClick={clearSchedule}>
            <Trash2 className="mr-1 h-4 w-4" /> Clear Selection
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-gray-800 bg-[#161616] p-4">
        <div className="mb-4 flex items-center">
          <div className="w-24 shrink-0" />
          <div className="flex flex-1">
            {Array.from({ length: 24 }).map((_, i) => (
              <HourLabel key={i} hour={i} />
            ))}
          </div>
        </div>

        {DISPLAY_ORDER.map((dayIndex) => (
          <DaySchedule
            key={dayIndex}
            dayName={DAYS_OF_WEEK[dayIndex]}
            dayOfWeek={dayIndex}
            scheduleMap={scheduleMap}
            viewWorkflowIndex={viewWorkflowIndex}
            workflows={workflows}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            isBlockSelected={isBlockSelected}
            isDragging={isDragging}
          />
        ))}

        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-400">
            {/* Generate a color for each workflow */}
            {workflows.map((workflow, index) => {
              const isSelected = index === viewWorkflowIndex;
              const isDefault = workflow.isDefault;
              return (
                <div
                  key={`workflow-${index}`}
                  className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 ${isSelected ? 'bg-gray-800' : ''}`}
                  onClick={() => setViewWorkflowIndex(index)}
                >
                  <div className={`h-3 w-3 rounded-full ${getWorkflowColor(index)}`} />
                  <span className="flex items-center gap-1">
                    {workflow.name}
                    {isDefault && <CheckCircle className="h-3 w-3 text-green-500" />}
                  </span>
                </div>
              );
            })}
            <div className="flex cursor-pointer items-center gap-2 rounded px-2 py-1">
              <div className="h-3 w-3 rounded-full border border-gray-700 bg-transparent" />
              {getDefaultWorkflowIndex() >= 0
                ? `Default Workflow (${workflows[getDefaultWorkflowIndex()]?.name})`
                : 'Default (None)'}
            </div>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-400">
          <Clock className="mr-2 inline-block h-4 w-4" />
          Click and drag to select time ranges for the workflow. The selected workflow will be
          active during these times.
        </div>
      </div>
    </div>
  );
};

const HourLabel: React.FC<{ hour: number }> = ({ hour }) => (
  <div className="flex-1 text-center text-xs text-gray-400">{hour}</div>
);

interface DayScheduleProps {
  dayName: string;
  dayOfWeek: number;
  scheduleMap: Record<number, Record<number, number[]>>;
  viewWorkflowIndex: number;
  workflows: WorkflowConfig[];
  onMouseDown: (dayIndex: number, blockIndex: number) => void;
  onMouseMove: (dayIndex: number, blockIndex: number) => void;
  onMouseEnter: (dayIndex: number, blockIndex: number) => void;
  isBlockSelected: (dayIndex: number, blockIndex: number) => boolean;
  isDragging: boolean;
}

const DaySchedule: React.FC<DayScheduleProps> = ({
  dayName,
  dayOfWeek,
  scheduleMap,
  viewWorkflowIndex,
  workflows,
  onMouseDown,
  onMouseMove,
  onMouseEnter,
  isBlockSelected,
  isDragging,
}) => {
  return (
    <div className="flex w-full items-center gap-4">
      <div className="w-24 shrink-0 text-gray-400">{dayName}</div>
      <div className="flex flex-1">
        {Array.from({ length: TOTAL_BLOCKS }).map((_, blockIdx) => {
          const assignedWorkflows = scheduleMap[dayOfWeek][blockIdx];
          const isCurrentWorkflow = assignedWorkflows.includes(viewWorkflowIndex);
          const hasOtherWorkflow = assignedWorkflows.length > 0 && !isCurrentWorkflow;
          const workflowIndex = hasOtherWorkflow ? assignedWorkflows[0] : viewWorkflowIndex;
          const workflowName = isCurrentWorkflow
            ? workflows[viewWorkflowIndex]?.name
            : hasOtherWorkflow
              ? workflows[assignedWorkflows[0]]?.name
              : '';

          // Get the color for the current workflow or selection
          const getBlockColor = () => {
            if (isBlockSelected(dayOfWeek, blockIdx) && isDragging) {
              // Use the color of the current workflow for selection
              return `${WORKFLOW_COLORS[viewWorkflowIndex % WORKFLOW_COLORS.length]} opacity-80`; // Selection highlight
            } else if (isCurrentWorkflow) {
              return WORKFLOW_COLORS[workflowIndex % WORKFLOW_COLORS.length];
            } else if (hasOtherWorkflow) {
              const otherIndex = workflows.findIndex((w) => w.name === workflowName);
              return otherIndex >= 0
                ? WORKFLOW_COLORS[otherIndex % WORKFLOW_COLORS.length]
                : 'bg-gray-600';
            } else {
              return 'bg-transparent'; // Unscheduled
            }
          };

          return (
            <div
              key={blockIdx}
              className={`h-8 flex-1 cursor-pointer border-b border-r border-t transition-colors ${getBlockColor()}`}
              title={
                isCurrentWorkflow
                  ? `${workflowName}`
                  : hasOtherWorkflow
                    ? `${workflowName}`
                    : `Unscheduled`
              }
              onMouseDown={() => onMouseDown(dayOfWeek, blockIdx)}
              onMouseMove={() => onMouseMove(dayOfWeek, blockIdx)}
              onMouseEnter={() => onMouseEnter(dayOfWeek, blockIdx)}
            />
          );
        })}
      </div>
    </div>
  );
};
